import type { TurnEvent } from './contract'

/**
 * A turn's Server-Sent Events, as TurnEvents (spec §12: one JSON object per `data:` frame, a `: ping`
 * comment every 15 s).
 *
 * The network cuts the body wherever it likes, so bytes are decoded as a stream (a character split across
 * two reads stays whole), text is kept until a blank line ends its frame, and a CR at the very end of a
 * read waits for the next one in case it is half of a CRLF. Per the SSE rules a frame is only delivered
 * once its blank line arrives: a stream that ends mid-frame drops that frame, and the caller's fallback
 * (the saved answer) takes over. A frame that is not a JSON event is skipped, never fatal.
 */
export async function* readTurnEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<TurnEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let text = ''
  let heldCr = false
  try {
    for (;;) {
      const { value, done } = await reader.read()
      let chunk = done ? decoder.decode() : decoder.decode(value, { stream: true })
      if (heldCr) {
        chunk = `\r${chunk}`
        heldCr = false
      }
      if (!done && chunk.endsWith('\r')) {
        heldCr = true
        chunk = chunk.slice(0, -1)
      }
      text += chunk.replace(/\r\n?/g, '\n')
      let end: number
      while ((end = text.indexOf('\n\n')) >= 0) {
        const event = parseFrame(text.slice(0, end))
        text = text.slice(end + 2)
        if (event) yield event
      }
      if (done) return
    }
  } finally {
    reader.releaseLock()
  }
}

/** One frame's event: its `data:` lines joined by newlines, parsed — or null for a heartbeat or anything else. */
function parseFrame(frame: string): TurnEvent | null {
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''))
    .join('\n')
  if (!data) return null
  try {
    const parsed: unknown = JSON.parse(data)
    if (typeof parsed === 'object' && parsed !== null && typeof (parsed as { type?: unknown }).type === 'string') return parsed as TurnEvent
  } catch {
    // Not JSON: skipped.
  }
  return null
}
